/**
 * <TutorLiveActionBar> — les gestes du centre du live, dans une barre flottante en bas au centre (molecule).
 *
 * Wajih, 04/11 : la tête du centre empilait trois lignes (l'élève, les gestes, les onglets). Les gestes
 * descendent ici, dans une pilule qui flotte au-dessus du contenu, comme la barre d'outils de Lovable ; en
 * haut ne restent que l'élève et les onglets.
 *
 * **Elle suit l'onglet ouvert** (`tutorLiveActionBarParts`, pur et testé) : sur une vue de l'activité, son
 * titre et son chrono, son statut, l'action qu'il attend, « Afficher à l'élève », « Présenter » et « Plus » ;
 * sur la visio ou les contrôles, « Ajouter une activité » seul. Le centre décide de ce qui s'y pose ; elle
 * ne fait que le montrer. Un centre étroit prend les mots courts (`compact`), et la pilule passe à la ligne
 * plutôt que de couper un geste.
 *
 * Contrôlée : chaque geste repart.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UIActivityDurationTone, type UIActivityStatus } from "../../../ports/primary/ui/types/activity"
import { type UITutorActivityAction } from "../../../ports/primary/ui/types/tutorEleves"
import { Badge } from "../../atoms/Badge"
import { Button } from "../../atoms/Buttons/Button"
import { type IconName } from "../../atoms/icons/Icon/iconRegistry"
import { Text } from "../../atoms/typography/Text"
import { shadowStyle } from "../../utils/shadow"
import { ActivityStatusBadge } from "../ActivityStatusBadge"
import {
    ACTIVITY_ACTIONS,
    ACTIVITY_SHOWN_LABEL,
    ACTIVITY_SHOWN_SHORT,
} from "../TutorLiveCenterHead/tutorActivityActions"

export type TutorLiveActionBarProps = {
    /** L'activité ouverte : son titre, et son chrono au ton du temps prévu. */
    info?: { title: string; chrono?: { label: string; tone?: UIActivityDurationTone } }
    status?: UIActivityStatus
    /** L'action qu'attend le statut (Valider, Redonner, Soumettre). */
    primary?: { action: UITutorActivityAction; onPress: () => void; pending?: boolean }
    /** « Afficher à l'élève » ; `shown` : il l'a devant lui. */
    show?: { onPress: () => void; pending?: boolean; shown?: boolean; pressableWhenShown?: boolean }
    /** « Présenter » (jamais « Arrêter » : pendant une présentation, la barre se retire). */
    present?: { onPress: () => void; pending?: boolean }
    /** « Ajouter une activité », en un geste (une vue sans activité). */
    onAdd?: () => void
    /** « Plus » : les autres gestes. */
    onOpenMenu?: () => void
    /** Un centre étroit : les mots courts, sans le titre de l'activité. Défaut `false`. */
    compact?: boolean
    testID?: string
}

const TEXTS = {
    bar: "Gestes sur l'activité",
    present: "Présenter",
    add: "Ajouter une activité",
    addShort: "Ajouter",
    more: "Plus d'actions sur l'activité",
    menu: "Plus",
}

/** Le chrono, au ton du temps prévu (comme `<TutorLiveCenterHead>`). */
const CHRONO_VARIANT = { on_time: "success", over: "warning" } as const
/** Le titre de l'activité cède sa place aux gestes. */
const INFO_MAX = 200

export function TutorLiveActionBar(props: TutorLiveActionBarProps) {
    const { info, status, primary, show, present, onAdd, onOpenMenu, compact = false, testID } = props
    const shown = show?.shown === true
    return (
        <View style={styles.pill} accessibilityRole="toolbar" accessibilityLabel={TEXTS.bar} testID={testID}>
            {info != null && !compact && (
                <View style={styles.info}>
                    <Text size="text-sm" weight="bold" color="text-strong" numberOfLines={1}>
                        {info.title}
                    </Text>
                    {info.chrono != null && (
                        <Badge
                            text={info.chrono.label}
                            variant={info.chrono.tone != null ? CHRONO_VARIANT[info.chrono.tone] : "neutral"}
                            iconName="stopwatch"
                            tinted
                            truncate={false}
                        />
                    )}
                </View>
            )}
            {status != null && !compact && <ActivityStatusBadge status={status} />}
            {primary != null && (
                <Action
                    label={ACTIVITY_ACTIONS[primary.action].label}
                    iconName={ACTIVITY_ACTIONS[primary.action].icon}
                    solid
                    pending={primary.pending === true}
                    onPress={primary.onPress}
                />
            )}
            {show != null && (
                <Action
                    label={shown ? ACTIVITY_SHOWN_LABEL : ACTIVITY_ACTIONS.show.label}
                    shortLabel={compact ? (shown ? ACTIVITY_SHOWN_SHORT : ACTIVITY_ACTIONS.show.short) : undefined}
                    iconName={shown ? undefined : ACTIVITY_ACTIONS.show.icon}
                    pending={show.pending === true}
                    disabled={shown && show.pressableWhenShown !== true}
                    onPress={show.onPress}
                />
            )}
            {present != null && (
                <Action
                    label={TEXTS.present}
                    iconName="chalkboard-teacher"
                    pending={present.pending === true}
                    onPress={present.onPress}
                />
            )}
            {onAdd != null && (
                <Action
                    label={TEXTS.add}
                    shortLabel={compact ? TEXTS.addShort : undefined}
                    iconName="plus"
                    pending={false}
                    onPress={onAdd}
                />
            )}
            {onOpenMenu != null && (
                <Button
                    label={TEXTS.menu}
                    iconName="ellipsis-v"
                    variant="ghost"
                    size="sm"
                    onPress={onOpenMenu}
                    accessibilityLabel={TEXTS.more}
                />
            )}
        </View>
    )
}

/** Un geste de la barre : le secondaire du livre, le principal pour l'action du statut. */
function Action(props: {
    label: string
    shortLabel?: string
    iconName?: IconName
    solid?: boolean
    pending: boolean
    disabled?: boolean
    onPress: () => void
}) {
    const { label, shortLabel, iconName, solid = false, pending, disabled = false, onPress } = props
    return (
        <Button
            label={shortLabel ?? label}
            accessibilityLabel={label}
            iconName={iconName}
            variant={solid ? "solid" : "outline"}
            size="sm"
            loading={pending}
            disabled={disabled}
            onPress={onPress}
        />
    )
}

const styles = StyleSheet.create((theme) => ({
    /** La pilule : elle flotte (l'ombre des surfaces qui flottent), et passe à la ligne plutôt que de couper. */
    pill: {
        flexDirection: "row" as const,
        flexWrap: "wrap" as const,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        columnGap: theme.spacing["2"],
        rowGap: theme.spacing["2"],
        maxWidth: "100%",
        paddingVertical: theme.spacing["2"],
        paddingHorizontal: theme.spacing["3"],
        borderRadius: theme.radius.xl,
        backgroundColor: theme.colors["surface-floating"],
        borderWidth: 1,
        borderColor: theme.colors["border-subtle"],
        ...shadowStyle(theme, "floating"),
    },
    info: {
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: theme.spacing["2"],
        flexShrink: 1,
        minWidth: 0,
        maxWidth: INFO_MAX,
        paddingRight: theme.spacing["1"],
    },
}))
